import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import ReactMarkdown from 'react-markdown';
import { MessageSquare, Send, X, Check, Loader2, Maximize2, Minimize2, ChevronRight, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import HelpDot from '@/components/ui/HelpDot';
import { apiFetch } from '@/lib/api';
import { chatInstructAtom } from '@/hooks/atoms';
import type { ChatMessage } from '@/hooks/useProduction';

interface ChatPanelProps {
  productionId: string;
  // Live messages streamed via SSE (from useProductionProgress state.chat).
  liveMessages: ChatMessage[];
}

// A proposal in any of these states is settled — its Confirm/Reject buttons are
// gone for good and an SSE replay (always "awaiting_confirm") must not revive them.
const RESOLVED_STATUSES = new Set(['confirmed', 'rejected', 'executing', 'done', 'error']);
// While a producer message waits for its answer, re-read the thread this often (the
// answer may come from the service, after the run's live stream has closed).
const POLL_MS = 4000;
const POLL_GIVE_UP_MS = 6 * 60 * 1000;

const LAUNCHER_CLS =
  'fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-primary ' +
  'px-4 py-3 text-primary-foreground shadow-lg hover:opacity-90';
const PANEL_CLS =
  'fixed bottom-5 right-5 z-40 flex max-w-[calc(100vw-2.5rem)] flex-col ' + 'rounded-lg border border-border bg-card shadow-2xl';
const INPUT_CLS =
  'flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 ' +
  'text-sm focus:outline-none focus:ring-1 focus:ring-ring';
// Markdown inside a Director bubble (lists, headings, inline code) — no typography plugin.
const MD_CLS =
  'space-y-2 break-words [&_code]:rounded [&_code]:bg-background/70 [&_code]:px-1 ' +
  '[&_code]:text-[12px] [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold ' +
  '[&_hr]:border-border [&_li]:ml-4 [&_ol]:list-decimal [&_ul]:list-disc';
const CHIP_CLS =
  'rounded-full border border-border bg-background px-2.5 py-1 text-xs text-foreground ' +
  'hover:border-primary hover:text-primary disabled:opacity-50';

function fromApi(m: Record<string, unknown>): ChatMessage {
  const list = (v: unknown) => (Array.isArray(v) ? (v as string[]) : undefined);
  return {
    id: m.id as string,
    role: m.role as ChatMessage['role'],
    text: (m.text as string) || '',
    kind: (m.kind as ChatMessage['kind']) || 'message',
    action: (m.action as ChatMessage['action']) || null,
    asset_ref: (m.asset_ref as string) || '',
    status: (m.status as string) || 'done',
    timestamp: (m.ts ? Date.parse(String(m.ts)) : 0) || Date.now(),
    turn: (m.turn as string) || undefined,
    reply_to: (m.reply_to as string) || undefined,
    choices: list(m.choices),
    attachments: list(m.attachments),
    steps: list(m.steps),
  };
}

/**
 * Live Director chat. The Director is an agent: it checks the production, looks at frames
 * and clips, and answers — its tool steps stream in while it works — then proposes changes
 * as Confirm / Reject cards (nothing regenerates before a click) and offers quick replies.
 */
export default function ChatPanel({ productionId, liveMessages }: ChatPanelProps) {
  const { t } = useTranslation('production');
  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [instruct, setInstruct] = useAtom(chatInstructAtom);
  const [pendingRef, setPendingRef] = useState<{ assetRef: string; label: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const loadThread = useCallback(async () => {
    try {
      const resp = await apiFetch(`/api/chat/${productionId}`);
      if (!resp.ok) return;
      const data = await resp.json();
      const hist: ChatMessage[] = (data.messages || []).map(fromApi);
      setMessages((prev) => {
        // Keep live activity lines (not stored server-side) and local resolutions.
        const byId = new Map(prev.map((m) => [m.id, m]));
        const merged = hist.map((h) => {
          const local = byId.get(h.id);
          return local && RESOLVED_STATUSES.has(local.status || '') && h.status === 'awaiting_confirm'
            ? { ...h, status: local.status }
            : h;
        });
        const ids = new Set(merged.map((m) => m.id));
        return [...merged, ...prev.filter((m) => !ids.has(m.id) && m.kind === 'activity')];
      });
    } catch {
      // history is best-effort
    }
  }, [productionId]);

  // Load history once on mount.
  useEffect(() => {
    loadThread();
  }, [loadThread]);

  // Merge live SSE messages by id (dedupe against history + prior stream).
  // The SSE stream REPLAYS its whole log on every (re)connect, and a proposal
  // event always carries status "awaiting_confirm" — so a naive merge would keep
  // resurrecting the Confirm/Reject buttons after the user already resolved it.
  // Never downgrade a proposal that is already resolved (locally or per history).
  useEffect(() => {
    if (!liveMessages.length) return;
    setMessages((prev) => {
      const byId = new Map(prev.map((m) => [m.id, m]));
      for (const m of liveMessages) {
        const existing = byId.get(m.id);
        if (existing && RESOLVED_STATUSES.has(existing.status || '') && m.status === 'awaiting_confirm') {
          byId.set(m.id, { ...existing, ...m, status: existing.status });
        } else {
          byId.set(m.id, { ...existing, ...m });
        }
      }
      return Array.from(byId.values());
    });
  }, [liveMessages]);

  // A gallery tile asked to "instruct about this" — open + prime the asset ref.
  useEffect(() => {
    if (instruct) {
      setPendingRef({ assetRef: instruct.assetRef, label: instruct.label });
      setOpen(true);
      setInstruct(null);
    }
  }, [instruct, setInstruct]);

  const visible = useMemo(() => messages.filter((m) => m.kind !== 'activity'), [messages]);

  // The newest producer message still waiting for the Director: nothing from the
  // Director after it (or pointing at it), sent in the last few minutes.
  const waitingFor = useMemo(() => {
    let idx = -1;
    for (let i = visible.length - 1; i >= 0; i -= 1) {
      if (visible[i].role === 'producer') {
        idx = i;
        break;
      }
    }
    if (idx < 0) return null;
    const last = visible[idx];
    const answered =
      visible.slice(idx + 1).some((m) => m.role === 'director') ||
      visible.some((m) => m.role === 'director' && m.reply_to === last.id);
    if (answered || Date.now() - last.timestamp > POLL_GIVE_UP_MS) return null;
    return last;
  }, [visible]);

  const liveSteps = useMemo(
    () => (waitingFor ? messages.filter((m) => m.kind === 'activity' && m.turn === `turn-${waitingFor.id}`) : []),
    [messages, waitingFor],
  );

  // Poll while waiting (the answer may arrive without the live stream).
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!waitingFor) {
      setGaveUp(false);
      return;
    }
    const started = Date.now();
    const id = window.setInterval(() => {
      if (Date.now() - started > POLL_GIVE_UP_MS) {
        setGaveUp(true);
        window.clearInterval(id);
        return;
      }
      loadThread();
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [waitingFor, loadThread]);

  // Auto-scroll to newest.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [visible, liveSteps, open]);

  const sendText = useCallback(
    async (raw: string) => {
      const body = raw.trim();
      if (!body || sending) return;
      setSending(true);
      try {
        const resp = await apiFetch(`/api/chat/${productionId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: body, asset_ref: pendingRef?.assetRef || '' }),
        });
        // Render the producer's own message immediately rather than waiting on the SSE
        // echo (which never arrives if the stream isn't actively connected). Use the
        // server's message_id so the later echo dedupes instead of duplicating.
        let mid = `producer-${Date.now()}`;
        try {
          const data = await resp.json();
          if (data?.message_id) mid = data.message_id;
        } catch {
          // ignore — fall back to the temp id
        }
        setMessages((prev) =>
          prev.some((m) => m.id === mid)
            ? prev
            : [
                ...prev,
                {
                  id: mid,
                  role: 'producer',
                  text: body,
                  kind: 'message',
                  asset_ref: pendingRef?.assetRef || '',
                  status: 'done',
                  timestamp: Date.now(),
                },
              ],
        );
        setText('');
        setPendingRef(null);
      } catch {
        // leave the text so the user can retry
      } finally {
        setSending(false);
      }
    },
    [sending, productionId, pendingRef],
  );

  const confirm = useCallback(
    async (messageId: string, approve: boolean) => {
      // Optimistic: flip the card immediately.
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, status: approve ? 'confirmed' : 'rejected' } : m)));
      try {
        await apiFetch(`/api/chat/${productionId}/confirm`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message_id: messageId, approve }),
        });
      } catch {
        // revert on failure
        setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, status: 'awaiting_confirm' } : m)));
      }
    },
    [productionId],
  );

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className={LAUNCHER_CLS}>
        <MessageSquare className="h-5 w-5" />
        <span className="text-sm font-medium">{t('chat.title')}</span>
      </button>
    );
  }

  const lastDirector = [...visible].reverse().find((m) => m.role === 'director' && m.choices?.length);
  const starters = [t('chat.starter1'), t('chat.starter2'), t('chat.starter3')];

  return (
    <div className={`${PANEL_CLS} ${wide ? 'w-[760px]' : 'w-[440px]'}`}>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">{t('chat.panelTitle')}</span>
          <HelpDot>{t('chat.why')}</HelpDot>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setWide((w) => !w)}
            className="text-muted-foreground hover:text-foreground"
            title={wide ? t('chat.collapse') : t('chat.expand')}
          >
            {wide ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </button>
          <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Thread */}
      <div
        ref={scrollRef}
        className={`flex min-h-[240px] flex-col gap-2 overflow-y-auto p-3 ${wide ? 'max-h-[75vh]' : 'max-h-[60vh]'}`}
      >
        {visible.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-1 py-6 text-center">
            <p className="text-xs text-muted-foreground">{t('chat.empty')}</p>
            <div className="flex flex-wrap justify-center gap-1.5">
              {starters.map((s) => (
                <button key={s} className={CHIP_CLS} onClick={() => sendText(s)} disabled={sending}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {visible.map((m) => (
          <ChatBubble key={m.id} msg={m} productionId={productionId} onConfirm={confirm} />
        ))}
        {waitingFor && (
          <div className="flex justify-start">
            <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
              {gaveUp ? (
                t('chat.noAnswer')
              ) : (
                <>
                  <div className="flex items-center gap-1.5 font-medium">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    {t('chat.thinking')}
                  </div>
                  {liveSteps.map((s) => (
                    <div key={s.id} className="mt-1 flex items-center gap-1.5 font-mono text-[11px]">
                      <Wrench className="h-3 w-3 shrink-0" />
                      <span className="truncate">{s.text}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
        {!waitingFor && lastDirector?.choices && lastDirector === visible[visible.length - 1] && (
          <div className="flex flex-wrap gap-1.5 pl-1">
            {lastDirector.choices.map((c) => (
              <button key={c} className={CHIP_CLS} onClick={() => sendText(c)} disabled={sending}>
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-border p-2.5">
        {pendingRef && (
          <div className="mb-1.5 flex items-center gap-1.5 rounded bg-muted px-2 py-1 text-xs">
            <span className="text-muted-foreground">Re:</span>
            <span className="font-mono font-medium truncate">{pendingRef.label}</span>
            <button onClick={() => setPendingRef(null)} className="ml-auto text-muted-foreground hover:text-foreground">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendText(text);
              }
            }}
            placeholder={t('chat.placeholder')}
            rows={wide ? 3 : 1}
            className={INPUT_CLS}
          />
          <Button size="sm" onClick={() => sendText(text)} disabled={sending || !text.trim()}>
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Attachment({ productionId, path }: { productionId: string; path: string }) {
  const { t } = useTranslation('production');
  const src = `/api/assets/${productionId}/${path}`;
  const lower = path.toLowerCase();
  const name = path.split('/').pop() || path;
  if (/\.(mp4|mov|webm)$/.test(lower)) {
    return <video src={src} controls preload="metadata" className="w-full rounded border border-border" />;
  }
  if (/\.(wav|mp3|ogg|flac)$/.test(lower)) {
    return <audio src={src} controls preload="none" className="w-full" />;
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" title={t('chat.openAsset', { path })} className="block">
      <img
        src={src}
        alt={name}
        loading="lazy"
        className="w-full rounded border border-border object-cover hover:border-primary"
      />
      <div className="mt-0.5 truncate font-mono text-[10px] text-muted-foreground">{name}</div>
    </a>
  );
}

function ChatBubble({
  msg,
  productionId,
  onConfirm,
}: {
  msg: ChatMessage;
  productionId: string;
  onConfirm: (id: string, approve: boolean) => void;
}) {
  const { t } = useTranslation('production');
  const [showSteps, setShowSteps] = useState(false);
  const isProducer = msg.role === 'producer';
  const isProposal = msg.kind === 'proposal';
  const steps = msg.steps || [];
  const attachments = msg.attachments || [];

  return (
    <div className={isProducer ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={`max-w-[88%] rounded-lg px-3 py-2 text-sm ${
          isProducer
            ? 'bg-primary text-primary-foreground'
            : msg.kind === 'result'
              ? 'bg-green-500/10 text-foreground border border-green-500/30'
              : 'bg-muted text-foreground'
        }`}
      >
        {!isProducer && (
          <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Director</div>
        )}
        {steps.length > 0 && (
          <div className="mb-1">
            <button
              onClick={() => setShowSteps((s) => !s)}
              className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className={`h-3 w-3 transition-transform ${showSteps ? 'rotate-90' : ''}`} />
              {t('chat.usedTools', { count: steps.length })}
            </button>
            {showSteps && (
              <ul className="mt-1 space-y-0.5 border-l border-border pl-2 font-mono text-[11px] text-muted-foreground">
                {steps.map((s, i) => (
                  <li key={i} className="truncate">
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {msg.text &&
          (isProducer ? (
            <div className="whitespace-pre-wrap">{msg.text}</div>
          ) : (
            <div className={MD_CLS}>
              <ReactMarkdown>{msg.text}</ReactMarkdown>
            </div>
          ))}

        {attachments.length > 0 && (
          <div className={`mt-2 grid gap-2 ${attachments.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
            {attachments.map((p) => (
              <Attachment key={p} productionId={productionId} path={p} />
            ))}
          </div>
        )}

        {isProposal && (
          <div className="mt-2 rounded-md border border-border bg-background/60 p-2">
            {msg.action?.summary && <div className="mb-2 text-xs font-medium">{msg.action.summary}</div>}
            {msg.status === 'awaiting_confirm' ? (
              <div className="flex gap-2">
                <Button size="sm" className="h-7 flex-1" onClick={() => onConfirm(msg.id, true)}>
                  <Check className="mr-1 h-3.5 w-3.5" />
                  {t('chat.confirm')}
                </Button>
                <Button size="sm" variant="outline" className="h-7 flex-1" onClick={() => onConfirm(msg.id, false)}>
                  <X className="mr-1 h-3.5 w-3.5" />
                  {t('chat.reject')}
                </Button>
              </div>
            ) : (
              <div className="text-xs font-medium text-muted-foreground">
                {msg.status === 'confirmed' ? t('chat.confirmed') : msg.status === 'rejected' ? t('chat.rejected') : msg.status}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
